import { gql } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import { omit } from 'ramda';
import { compact, titleize } from '@moda/portal-stanchions';
import { CategoryTreeFragment } from '../generated/types';
import { useUrlParams } from './useUrlParams';
import { usePLPParams, FILTER_KEYS } from './usePLPParams';

type NonRecursiveCategoryTree = NonNullable<CategoryTreeFragment['categoryTree']>;

// graphql doesn't support recursive queries, making the interface recursive here, this makes it easier to work with
export interface CategoryTree extends Omit<NonRecursiveCategoryTree, 'children'> {
  children?: (CategoryTree | null)[] | null;
}

export const CATEGORY_TREE_FRAGMENT = gql`
  fragment CategoryTreeFields on CategoryTree {
    name
    slug
    totalItems: total_items
  }
  fragment CategoryTreeFragment on CollectionResult {
    categoryTree: category_tree {
      ...CategoryTreeFields
      children {
        ...CategoryTreeFields
        children {
          ...CategoryTreeFields
          children {
            ...CategoryTreeFields
            children {
              ...CategoryTreeFields
            }
          }
        }
      }
    }
  }
`;

const DEFAULT_TREE: CategoryTree = {
  __typename: 'CategoryTree',
  name: '',
  slug: 'all',
  totalItems: 0,
  children: null
};

const LEVELS = ['root', 'vertical', 'category', 'subcategory'] as const;

const isEqual = (slugA?: string, slugB?: string) => (slugA ?? '') === (slugB ?? '');

/**
 * Hide 'Women' from the category filters on the UI. See ECOM-2377.
 */
const isNodeHidden = (node: CategoryTree) => node.slug === 'women';

const getFirstVisibleAncestor = (
  tree: CategoryTree,
  node: CategoryTree | null
): CategoryTree | null => {
  if (!node) return null;
  return isNodeHidden(node) ? getFirstVisibleAncestor(tree, getParentNode(tree, node)) : node;
};

const getVisibleChildren = (node: CategoryTree | null) =>
  compact(
    compact(node?.children)
      .map(child => (isNodeHidden(child) ? child.children : child))
      .flat()
  );

const getSelectedNodeForCategory = (
  node: CategoryTree,
  categories: { root?: undefined; vertical?: string; category?: string; subcategory?: string },
  category: (typeof LEVELS)[number],
  level = 0
): CategoryTree | null => {
  if (LEVELS[level] === category) return node;

  const selectedChild = node.children?.find(child =>
    isEqual(child?.slug, categories[LEVELS[level + 1]])
  );
  return selectedChild
    ? getSelectedNodeForCategory(selectedChild, categories, category, level + 1)
    : null;
};

const getSelectedNode = (
  node: CategoryTree,
  categories: { root?: undefined; vertical?: string; category?: string; subcategory?: string },
  level = 0
): CategoryTree => {
  const selectedChild = node.children?.find(child =>
    isEqual(child?.slug, categories[LEVELS[level + 1]])
  );
  return selectedChild ? getSelectedNode(selectedChild, categories, level + 1) : node;
};

const getActiveNode = (
  node: CategoryTree,
  categories: { root?: undefined; vertical?: string; category?: string; subcategory?: string },
  hasAttributes: boolean,
  level = 0
): CategoryTree => {
  const activeChild = node.children?.find(child =>
    isEqual(child?.slug, categories[LEVELS[level + 1]])
  );
  return activeChild &&
    ((activeChild.children || []).length > 0 ||
      (LEVELS[level + 1] === 'subcategory' && hasAttributes))
    ? getActiveNode(activeChild, categories, hasAttributes, level + 1)
    : node;
};

const getParentNode = (tree: CategoryTree, node: CategoryTree): CategoryTree | null => {
  if (!tree.children) return null;
  if (tree.children.includes(node)) return tree;
  return tree.children.map(child => child && getParentNode(child, node)).find(Boolean) || null;
};

const getPathToNode = (tree: CategoryTree, node: CategoryTree): CategoryTree[] => {
  const parent = getParentNode(tree, node);
  return parent ? [...getPathToNode(tree, parent), node] : [node];
};

const getNodeParams = (tree: CategoryTree, node: CategoryTree) => {
  const path = getPathToNode(tree, node);
  return LEVELS.reduce(
    (result, type, index) => (type === 'root' ? result : { ...result, [type]: path[index]?.slug }),
    {}
  );
};

const CATEGORY_NODE_HREF_OMITTED_KEYS = ['attributes', 'page'];

export const useCategoryTree = (categoryTree: CategoryTree | null, hasAttributes = false) => {
  const tree = useMemo(
    () => (categoryTree ? { ...categoryTree, name: '' } : DEFAULT_TREE),
    [categoryTree]
  );
  const { params: urlParams, getHrefFor } = useUrlParams();
  const {
    params: { vertical, category, subcategory }
  } = usePLPParams();

  const categories = useMemo(
    () => ({ vertical, category, subcategory }),
    [vertical, category, subcategory]
  );

  const rootNode = useMemo(
    () => getSelectedNodeForCategory(tree, categories, 'root'),
    [tree, categories]
  );

  const verticalNode = useMemo(
    () => getSelectedNodeForCategory(tree, categories, 'vertical'),
    [tree, categories]
  );

  const categoryNode = useMemo(
    () => getSelectedNodeForCategory(tree, categories, 'category'),
    [tree, categories]
  );

  const subcategoryNode = useMemo(
    () => getSelectedNodeForCategory(tree, categories, 'subcategory'),
    [tree, categories]
  );

  const selectedNodes = useMemo(
    () => ({
      root: rootNode,
      vertical: verticalNode,
      category: categoryNode,
      subcategory: subcategoryNode
    }),
    [rootNode, verticalNode, categoryNode, subcategoryNode]
  );

  const selectedCategoryNode = useMemo(
    () => getFirstVisibleAncestor(tree, getSelectedNode(tree, categories)),
    [tree, categories]
  );

  const activeCategoryNode = useMemo(
    () => getFirstVisibleAncestor(tree, getActiveNode(tree, categories, hasAttributes)),
    [tree, categories, hasAttributes]
  );

  const parentCategoryNode = useMemo(
    () =>
      getFirstVisibleAncestor(tree, activeCategoryNode && getParentNode(tree, activeCategoryNode)),
    [tree, activeCategoryNode]
  );

  const parentCategoryName = useMemo(
    () => titleize(parentCategoryNode?.name || 'All Results'),
    [parentCategoryNode]
  );

  const getCategoryNodeHref = useCallback(
    (
      node: CategoryTree,
      { removeFilters }: { removeFilters: boolean } = { removeFilters: false }
    ) => {
      try {
        return getHrefFor({
          ...omit(
            [
              ...LEVELS.slice(1),
              ...CATEGORY_NODE_HREF_OMITTED_KEYS,
              ...(removeFilters ? FILTER_KEYS : [])
            ],
            urlParams
          ),
          ...getNodeParams(tree, node)
        });
      } catch {
        return null;
      }
    },
    [tree, urlParams, getHrefFor]
  );

  const topLevelNode = useMemo(
    () =>
      [verticalNode, categoryNode, subcategoryNode].find(
        node => node && getCategoryNodeHref(node)
      ) || rootNode,
    [rootNode, verticalNode, categoryNode, subcategoryNode, getCategoryNodeHref]
  );

  const isTopLevel = useMemo(
    () =>
      activeCategoryNode == null ||
      parentCategoryNode == null ||
      getCategoryNodeHref(parentCategoryNode) == null ||
      getCategoryNodeHref(parentCategoryNode) == getCategoryNodeHref(activeCategoryNode),
    [parentCategoryNode, getCategoryNodeHref, activeCategoryNode]
  );

  return {
    activeCategoryNode,
    getCategoryNodeHref,
    getVisibleChildren,
    isNodeHidden,
    isTopLevel,
    parentCategoryName,
    parentCategoryNode,
    selectedCategoryNode,
    selectedNodes,
    topLevelNode
  };
};
